/**
 * Spiegel von api `App\Service\Editor\EditorConfigDefaults`. Das Backend liefert im
 * Bootstrap bereits `editorConfigByCategory` (serverseitig gemerged und typisiert);
 * diese Defaults greifen nur, wenn ein Produkt/Kategorie ohne diese Struktur ankommt
 * (alter Cache) oder wenn client-seitig aus rohen editorConfig-Objekten aufgelöst wird.
 */
export type EditorMode = 'simple' | 'design'

export type EditorFeatures = {
  layers: boolean
  text: boolean
  stickers: boolean
  templates: boolean
  collage: boolean
  filters: boolean
  backgrounds: boolean
  shapes: boolean
  freeColor: boolean
}

export type EditorLimits = {
  maxLayers: number
  maxTextLayers: number
  maxImageLayers: number
  minDpiWarn: number
  minDpiBlock: number
}

export type EditorCanvasConfig = {
  shape: 'rect' | 'rounded' | 'circle' | 'hexagon' | 'custom'
  cornerRadiusMm: number
  bleedMm: number
  safeMarginMm: number
  dpi: number
  roundedOptionRadiusMm: number
  showGuides: boolean
  snapEnabled: boolean
  collageGutterMm: number
  shapeParams: Record<string, unknown>
  /** 1 = eine Druckfläche, 2 = Vorder- und Rückseite (Postkarte) */
  sides: 1 | 2
  /** Nicht sichtbare/Griff-/Naht-Zonen in mm (z. B. Tasse) */
  zones: Array<{ kind: 'hidden' | 'handle' | 'seam'; xMm: number; yMm: number; wMm: number; hMm: number; label?: string }>
}

export type ResolvedEditorConfig = {
  mode: EditorMode
  /** Nur bei mode 'simple': Designer zusätzlich zur Auswahl anbieten. */
  designAvailable: boolean
  features: EditorFeatures
  limits: EditorLimits
  canvas: EditorCanvasConfig
  allowedTemplateCategoryIds: number[]
  allowedAssetCategoryIds: number[]
  allowedFontIds: string[]
  defaultTemplateId: string | null
  colorHintText: string
  preview3d: Record<string, unknown>
  /** Bögen (Fotosticker, Passbilder): Bogen-Vorlage je Größe/Option */
  sheets: SheetAssignment[]
  /** slots = Einzelbilder je Platz, sheet = fertiger Bogen, both = beides */
  sheetExport: 'slots' | 'sheet' | 'both'
}

export type SheetAssignment = {
  templateId: string
  sizeIds: number[]
  optionVariantIds: number[]
  optionValues: string[]
  label: string
}

export const EDITOR_CONFIG_DEFAULTS: ResolvedEditorConfig = {
  mode: 'simple',
  designAvailable: false,
  features: {
    layers: true,
    text: true,
    stickers: true,
    templates: true,
    collage: true,
    filters: true,
    backgrounds: true,
    shapes: false,
    freeColor: true,
  },
  limits: {
    maxLayers: 30,
    maxTextLayers: 8,
    maxImageLayers: 20,
    minDpiWarn: 150,
    minDpiBlock: 72,
  },
  canvas: {
    shape: 'rect',
    cornerRadiusMm: 0,
    bleedMm: 0,
    safeMarginMm: 0,
    dpi: 300,
    roundedOptionRadiusMm: 5,
    showGuides: true,
    snapEnabled: true,
    collageGutterMm: 3,
    shapeParams: {},
    sides: 1,
    zones: [],
  },
  allowedTemplateCategoryIds: [],
  allowedAssetCategoryIds: [],
  allowedFontIds: [],
  defaultTemplateId: null,
  colorHintText: 'Bildschirmfarben können vom Druck abweichen. Wir drucken farbverbindlich in sRGB; Monitore sind selten kalibriert.',
  preview3d: {},
  sheets: [],
  sheetExport: 'slots',
}
